import { Capabilities } from '@/lib/api/types.gen';
import { cn } from '@/lib/utils';
import { Eye, LucideIcon, Play, Volume2 } from 'lucide-react';
import { ComponentProps } from 'react';
import { Badge, BadgeProps } from '../ui/badge';
import React from 'react';

type CapabilityName = keyof Capabilities;

const CapabilitiesConfig = {
  canLivestream: {
    icon: Eye,
    label: 'Live',
    className: 'text-blue-300',
  },
  canPlayback: {
    icon: Play,
    label: 'Playback',
    className: 'text-purple-300',
  },
  canTalkdown: {
    icon: Volume2,
    label: 'Talkdown',
    className: 'text-orange-300',
  },
} satisfies Partial<Record<CapabilityName, { icon: LucideIcon; label: string; className: string }>>;

export type DeviceCapabilitiesBadgeProps = {
  type: keyof typeof CapabilitiesConfig;
} & ComponentProps<'div'>;

export const DeviceCapabilitiesBadge = ({ type, ...rest }: DeviceCapabilitiesBadgeProps) => {
  const { icon: Icon, label, className } = CapabilitiesConfig[type];

  return (
    <div {...rest} className={cn('flex items-center text-xs', className, rest.className)}>
      <Icon className="mr-1 h-3 w-3" />
      {label}
    </div>
  );
};

export const DeviceCapabilitiesGroup = (
  props: BadgeProps & {
    capabilities?: Capabilities;
  }
) => {
  const children = props.capabilities
    ? Object.entries(props.capabilities)
        .filter((entry): entry is [keyof typeof CapabilitiesConfig, true] => {
          const [key, value] = entry;

          return key in CapabilitiesConfig && value;
        })
        .map(([key]) => {
          return <DeviceCapabilitiesBadge key={key} type={key} />;
        })
    : React.Children.toArray(props.children);

  if (children.length === 0) return null;

  return (
    <Badge
      {...props}
      variant="outline"
      className={cn('border-gray-600 bg-gray-800/50 text-xs', props.className)}
    >
      <div className="flex items-center gap-2">
        {children.map((child, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="text-gray-500">•</span>}
            {child}
          </React.Fragment>
        ))}
      </div>
    </Badge>
  );
};
